/** Returns raw initData for server-side validation (never trust client-side). */
export function getTelegramInitData(): string {
  return window.Telegram?.WebApp?.initData?.trim() ?? "";
}

export function isTelegramWebApp(): boolean {
  return Boolean(getTelegramInitData());
}

/** AP3R3R1G1 — one shared wait per page lifetime (no stacked 2s/5s/10s loops). */
let sharedInitDataPromise: Promise<string> | null = null;
let sharedInitDataResolved: string | null = null;

/**
 * COMMERCE-CANARY-163A-R2B — ready/expand then short retry until initData is present.
 * Does not invent identity; returns empty string if Telegram never supplies initData.
 * AP3R3R1G1 — concurrent callers share one in-flight wait.
 */
export async function waitForTelegramInitData(maxMs = 2500): Promise<string> {
  if (sharedInitDataResolved) return sharedInitDataResolved;
  const existing = getTelegramInitData();
  if (existing) {
    sharedInitDataResolved = existing;
    return existing;
  }
  if (!sharedInitDataPromise) {
    const budget = Math.max(0, maxMs);
    sharedInitDataPromise = (async () => {
      try {
        const webApp = window.Telegram?.WebApp;
        webApp?.ready?.();
        webApp?.expand?.();
      } catch {
        /* ignore */
      }
      const started = Date.now();
      while (Date.now() - started < budget) {
        const next = getTelegramInitData();
        if (next) {
          sharedInitDataResolved = next;
          return next;
        }
        await new Promise((r) => setTimeout(r, 100));
      }
      const final = getTelegramInitData();
      sharedInitDataResolved = final;
      return final;
    })().finally(() => {
      if (!sharedInitDataResolved) sharedInitDataPromise = null;
    });
  }
  return sharedInitDataPromise;
}

/** Test/reset hook — clears shared Telegram init cache. */
export function resetSharedTelegramInitDataForTests(): void {
  sharedInitDataPromise = null;
  sharedInitDataResolved = null;
}

/** Apply Telegram theme CSS variables and call ready/expand when available. */
export function bootstrapTelegramWebApp(): void {
  const webApp = window.Telegram?.WebApp;
  if (!webApp) return;

  webApp.ready();
  webApp.expand();

  const scheme = webApp.colorScheme ?? "light";
  document.documentElement.dataset["tgColorScheme"] = scheme;

  for (const [key, value] of Object.entries(webApp.themeParams ?? {})) {
    if (!value) continue;
    document.documentElement.style.setProperty(
      `--tg-theme-${key.replace(/_/g, "-")}`,
      String(value),
    );
  }
}
